<template>
  <Spyglass v-if="!isOnline || overrideEula" />

  <UILoadingScreen
    v-else-if="fetchingAcceptances || fetchingActivatedEulas"
    :loading="true"
    loadingMessage="Loading..."
  />

  <section v-else>
    <AppHeader />
    <EulaAgreement
      v-if="activeEula"
      :disable="false"
      title="Spyglass End User License Agreement"
      :eula="activeEula"
      @accept="handleAcceptEula"
    />
  </section>
</template>

<script setup lang="ts">
import useUserInfo from "@/composables/auth/useUserInfo";
import { useEulaStore } from "@/store";
import Spyglass from "@/views/Spyglass.vue";
import { useOnline } from "@vueuse/core";
import {
  useGetActivatedEulaQuery,
  useGetUserEulaAcceptancesQuery,
  useAcceptEulaMutation
} from "gql/api";
import { computed, watchEffect } from "vue";
import EulaAgreement from "./EulaAgreement.vue";
import { useToast } from "primevue/usetoast";
import AppHeader from "./AppHeader.vue";
import UILoadingScreen from "@/components/UILoadingScreen.vue";

const isOnline = useOnline();
const user = useUserInfo();
const { add: popToast } = useToast();
const { eulaAccepted, setEulaAccepted } = useEulaStore();

const activeEula = computed(() => activatedEulas.value?.eula[0]);
const overrideEula = computed(
  () =>
    activatedEulas.value?.eula.length === 0 ||
    !user.is_customer ||
    eulaAccepted.value
);

const {
  data: activatedEulas,
  fetching: fetchingActivatedEulas,
  executeQuery: refetchEulas
} = useGetActivatedEulaQuery({ requestPolicy: "network-only" });
const { executeMutation: acceptEula } = useAcceptEulaMutation();

const { data: userEulaAcceptances, fetching: fetchingAcceptances } =
  useGetUserEulaAcceptancesQuery({
    variables: computed(() => ({ id: activeEula.value?.id })),
    pause: computed(() => !activeEula.value?.id),
    requestPolicy: "network-only"
  });

const handleAcceptEula = async (acceptedId: string) => {
  await refetchEulas();
  if (acceptedId === activeEula.value?.id) {
    await acceptEula({ eulaId: acceptedId });
    setEulaAccepted();
  } else {
    popToast({
      severity: "info",
      detail:
        "The current agreement has updated. Please accept the new agreement to continue.",
      summary: "Notice"
    });
  }
};

watchEffect(() => {
  if (
    userEulaAcceptances.value?.user_eula_acceptance.some(
      eula => eula.eula_id === activeEula.value?.id
    )
  ) {
    setEulaAccepted();
  }
});
</script>
